import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReportIcon from '@mui/icons-material/Report';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { useCurrencies, useMaterialsSTO, useMaterialUOMs } from '@/hooks/useLookup';
import type { Material, Product } from '@/types';

interface Props {
  readOnly?: boolean;
  onProductDeleted?: (product: Product) => void;
}

const EMPTY_LINE: Product = {
  lineNumber: 1,
  itemNumber: '',
  materialCode: '',
  description: '',
  materialDescription: '',
  quantity: 0,
  salesUOM: '',
  unitOfMeasure: '',
  customerMaterialNumber: '',
  gomMaterialCode: '',
  gomMaterialDescription: '',
  batchNumber: '',
  plantCode: '',
  storageLocation: '',
  price: undefined,
  currency: '',
  perAmount: undefined,
  perUoM: '',
};

const tableCellSx = {
  padding: '10px 0 10px 16px',
  verticalAlign: 'middle',
  height: '56px',
};

const formatNumber = (value?: number, fractionDigits = 3) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '-';
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const formatQuantity = (value?: number) => {
  if (value == null || Number.isNaN(Number(value))) {
    return '-';
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 3,
  });
};

const stripLeadingZeros = (value?: string) => {
  if (!value) {
    return '';
  }

  const normalized = String(value).trim();
  if (!/^\d+$/.test(normalized)) {
    return normalized;
  }

  const stripped = normalized.replace(/^0+/, '');
  return stripped === '' ? '0' : stripped;
};

const firstNonEmpty = (...values: Array<unknown>) => {
  for (const value of values) {
    const normalized = String(value ?? '').trim();
    if (normalized) {
      return normalized;
    }
  }

  return undefined;
};

const IGNORED_STATUSES = ['OC-Draft', 'IM-Recycle', 'MOT-Recycle', 'SP-Recycle'];

export const ProductTable: FC<Props> = ({ readOnly = false, onProductDeleted }) => {
  const { control, getValues, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'products' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogType, setStatusDialogType] = useState<'edit' | 'delete'>('edit');
  const [pendingAction, setPendingAction] = useState<'add' | 'edit' | 'delete' | null>(null);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<Product> | null>(null);
  const cancelStatusButtonRef = useRef<HTMLButtonElement | null>(null);

  const soldToNumber = useWatch({ control, name: 'soldToNumber' }) as string | undefined;
  const shipToNumber = useWatch({ control, name: 'shipToNumber' }) as string | undefined;
  const distributionChannel = useWatch({ control, name: 'distributionChannel' }) as string | undefined;
  const division = useWatch({ control, name: 'division' }) as string | undefined;
  const orderType = useWatch({ control, name: 'orderType' }) as string | undefined;
  const salesOrg = useWatch({ control, name: 'salesOrg' }) as string | undefined;
  const shippingCondition = useWatch({ control, name: 'shippingCondition' }) as string | undefined;
  const supplyPointCode = useWatch({ control, name: 'supplyPointCode' }) as string | undefined;
  const supplyPointId = useWatch({ control, name: 'supplyPointId' }) as string | undefined;
  const orderStatus = useWatch({ control, name: 'status' }) as string | undefined;
  const [productSearchInput, setProductSearchInput] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productDialogPage, setProductDialogPage] = useState(0);
  const [productDialogRowsPerPage, setProductDialogRowsPerPage] = useState(3);
  const [selectedDialogMaterial, setSelectedDialogMaterial] = useState<Material | null>(null);

  const [dialogParams, setDialogParams] = useState<{
    shipToNumber: string;
    distributionChannel: string | undefined;
    division: string | undefined;
    orderType: string | undefined;
    salesOrg: string | undefined;
    supplyPointId: string;
  } | null>(null);

  const isDialogOpen = dialogMode != null;
  const normalizedSupplyPointId = String(supplyPointId ?? '').trim();

  const activeShipToNumber = firstNonEmpty(dialogParams?.shipToNumber, shipToNumber);
  const activeDistributionChannel = firstNonEmpty(dialogParams?.distributionChannel, distributionChannel);
  const activeDivision = firstNonEmpty(dialogParams?.division, division);
  const activeOrderType = firstNonEmpty(dialogParams?.orderType, orderType);
  const activeSalesOrg = firstNonEmpty(dialogParams?.salesOrg, salesOrg);
  const activeSupplyPointId = firstNonEmpty(dialogParams?.supplyPointId, normalizedSupplyPointId);

  const materialsQuery = useMaterialsSTO({
    enabled: isDialogOpen,
    customerNumber: activeShipToNumber,
    distributionChannel: activeDistributionChannel,
    division: activeDivision,
    filter: productSearchTerm,
    orderType: activeOrderType,
    salesOrganization: activeSalesOrg,
    supplyPoint: activeSupplyPointId,
  });
  const materialUomsQuery = useMaterialUOMs(selectedDialogMaterial?.materialCode, isDialogOpen);
  const currenciesQuery = useCurrencies();

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(fields.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [fields.length, page, rowsPerPage]);

  useEffect(() => {
    if (statusDialogOpen) {
      cancelStatusButtonRef.current?.focus();
    }
  }, [statusDialogOpen]);

  useEffect(() => {
    if (!isDialogOpen) {
      setProductSearchInput('');
      setProductSearchTerm('');
      setProductDialogPage(0);
      setProductDialogRowsPerPage(3);
      setSelectedDialogMaterial(null);
    }
  }, [isDialogOpen]);

  useEffect(() => {
    const uoms = materialUomsQuery.data ?? [];
    if (!editDraft || !uoms.length) {
      return;
    }

    const uniqueUoms = Array.from(new Set(uoms.map((uom) => String(uom.alternativeUnitOfMeasure ?? '').trim()).filter(Boolean)));
    if (!uniqueUoms.length) {
      return;
    }

    const hasOrderUom = uniqueUoms.includes(String(editDraft.salesUOM ?? '').trim());
    const hasPerUom = uniqueUoms.includes(String(editDraft.perUoM ?? '').trim());

    if (!hasOrderUom || !hasPerUom) {
      const defaultUom = uniqueUoms[0];
      setEditDraft((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          salesUOM: hasOrderUom ? prev.salesUOM : defaultUom,
          perUoM: hasPerUom ? prev.perUoM : defaultUom,
        };
      });
    }
  }, [materialUomsQuery.data, editDraft]);

  const hasSupplyPoint = String(supplyPointCode ?? supplyPointId ?? '').trim().length > 0;
  const hasShippingCondition = String(shippingCondition ?? '').trim().length > 0;
  const hasSoldToNumber = String(soldToNumber ?? '').trim().length > 0;
  const hasShipToNumber = String(shipToNumber ?? '').trim().length > 0;

  const isAddProductDisabled = !hasSupplyPoint || !hasShippingCondition || !hasSoldToNumber || !hasShipToNumber;

  const pagedFields = useMemo(
    () => fields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [fields, page, rowsPerPage],
  );

  const dialogMaterials = useMemo(() => materialsQuery.data ?? [], [materialsQuery.data]);
  const pagedDialogMaterials = useMemo(
    () => dialogMaterials.slice(productDialogPage * productDialogRowsPerPage, productDialogPage * productDialogRowsPerPage + productDialogRowsPerPage),
    [dialogMaterials, productDialogPage, productDialogRowsPerPage],
  );
  const dialogCurrencies = useMemo(
    () => {
      const currencies = (currenciesQuery.data ?? [])
        .map((currency) => {
          const currencyValue = currency as unknown;
          if (typeof currencyValue === 'string') {
            return currencyValue.trim();
          }

          const currencyLookup = currencyValue as {
            code?: unknown;
            currencyCode?: unknown;
            currency?: unknown;
            Code?: unknown;
            CurrencyCode?: unknown;
            Currency?: unknown;
          };

          return String(
            currencyLookup.code
            ?? currencyLookup.currencyCode
            ?? currencyLookup.currency
            ?? currencyLookup.Code
            ?? currencyLookup.CurrencyCode
            ?? currencyLookup.Currency
            ?? '',
          ).trim();
        })
        .filter(Boolean);

      return Array.from(new Set(['USD', ...currencies]));
    },
    [currenciesQuery.data],
  );
  const dialogUoms = useMemo(
    () => Array.from(new Set((materialUomsQuery.data ?? []).map((uom) => String(uom.alternativeUnitOfMeasure ?? '').trim()).filter(Boolean))),
    [materialUomsQuery.data],
  );
  const canSubmitDialog = Boolean(
    selectedDialogMaterial
    && Number(editDraft?.quantity ?? 0) > 0
    && String(editDraft?.salesUOM ?? '').trim()
    && String(editDraft?.currency ?? '').trim()
    && Number(editDraft?.price ?? 0) > 0
    && Number(editDraft?.perAmount ?? 0) > 0
    && String(editDraft?.perUoM ?? '').trim(),
  );

  const findConversionFactors = (orderUom: string, amountUom: string) => {
    const uoms = materialUomsQuery.data ?? [];
    const orderUomFactor = Number(
      uoms.find((uom) => String(uom.alternativeUnitOfMeasure ?? '').trim() === orderUom)?.conversionFactor ?? 1,
    );
    const amountUomFactor = Number(
      uoms.find((uom) => String(uom.alternativeUnitOfMeasure ?? '').trim() === amountUom)?.conversionFactor ?? 1,
    );

    if (orderUomFactor > amountUomFactor && amountUomFactor !== 1) {
      return {
        orderUomConversionFactor: 1 / orderUomFactor,
        amountUomConversionFactor: 1 / amountUomFactor,
      };
    }

    if (orderUomFactor > amountUomFactor) {
      return {
        orderUomConversionFactor: 1 / orderUomFactor,
        amountUomConversionFactor: amountUomFactor,
      };
    }

    if (orderUomFactor < amountUomFactor && orderUomFactor !== 1) {
      return {
        orderUomConversionFactor: 1 / orderUomFactor,
        amountUomConversionFactor: 1 / amountUomFactor,
      };
    }

    if (orderUomFactor < amountUomFactor) {
      return {
        orderUomConversionFactor: orderUomFactor,
        amountUomConversionFactor: 1 / amountUomFactor,
      };
    }

    if (orderUomFactor < 1 && amountUomFactor < 1) {
      return {
        orderUomConversionFactor: orderUomFactor,
        amountUomConversionFactor: amountUomFactor,
      };
    }

    if (orderUomFactor > 1 && amountUomFactor > 1) {
      return {
        orderUomConversionFactor: 1 / orderUomFactor,
        amountUomConversionFactor: 1 / amountUomFactor,
      };
    }

    return {
      orderUomConversionFactor: orderUomFactor,
      amountUomConversionFactor: amountUomFactor,
    };
  };

  const totalSaleText = useMemo(() => {
    const quantity = Number(editDraft?.quantity ?? 0);
    const price = Number(editDraft?.price ?? 0);
    const perAmount = Number(editDraft?.perAmount ?? 0);
    const currency = String(editDraft?.currency ?? '').trim();
    const orderUom = String(editDraft?.salesUOM ?? '').trim();
    const amountUom = String(editDraft?.perUoM ?? '').trim();

    if (!(quantity > 0) || !(price > 0) || !(perAmount > 0)) {
      return '- -';
    }

    let orderUomConversionFactor = 1;
    let amountUomConversionFactor = 1;

    if (materialUomsQuery.data?.length) {
      ({ orderUomConversionFactor, amountUomConversionFactor } = findConversionFactors(orderUom, amountUom));
    }

    const total = quantity * orderUomConversionFactor * (price / (perAmount * amountUomConversionFactor));
    return `${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
  }, [editDraft?.quantity, editDraft?.price, editDraft?.perAmount, editDraft?.currency, editDraft?.salesUOM, editDraft?.perUoM, materialUomsQuery.data]);

  const isDraftStatus = IGNORED_STATUSES.includes(orderStatus ?? '');

  const openStatusDialog = (type: 'edit' | 'delete', action: 'add' | 'edit' | 'delete') => {
    setStatusDialogType(type);
    setPendingAction(action);
    setStatusDialogOpen(true);
  };

  const getNextProductSequence = () => {
    const products = (getValues('products') as Product[] | undefined) ?? [];
    const maxItemNumber = products.reduce((maxValue, product) => {
      const numericValue = Number(product.itemNumber ?? product.lineNumber ?? 0);
      return Number.isFinite(numericValue) ? Math.max(maxValue, numericValue) : maxValue;
    }, 0);

    return maxItemNumber + 1;
  };

  const removeProductAtIndex = (index: number) => {
    const existingProduct = getValues(`products.${index}`) as Product | undefined;
    if (existingProduct) {
      onProductDeleted?.(existingProduct);
    }

    remove(index);
  };

  const openAddDialog = () => {
    setDialogParams({
      shipToNumber: firstNonEmpty(getValues('shipToNumber')) ?? '',
      distributionChannel: firstNonEmpty(getValues('distributionChannel')),
      division: firstNonEmpty(getValues('division')),
      orderType: firstNonEmpty(getValues('orderType'), getValues('orderTypeDescription')),
      salesOrg: firstNonEmpty(getValues('salesOrg')),
      supplyPointId: firstNonEmpty(getValues('supplyPointId'), getValues('supplyPointCode')) ?? '',
    });
    setDialogMode('add');
    setEditingIndex(null);
    setSelectedDialogMaterial(null);
    setEditDraft({
      ...EMPTY_LINE,
      lineNumber: 0,
      itemNumber: '',
      quantity: undefined,
      price: undefined,
      perAmount: undefined,
      currency: 'USD',
    });
  };

  const openEditDialog = (index: number) => {
    setDialogParams({
      shipToNumber: firstNonEmpty(getValues('shipToNumber')) ?? '',
      distributionChannel: firstNonEmpty(getValues('distributionChannel')),
      division: firstNonEmpty(getValues('division')),
      orderType: firstNonEmpty(getValues('orderType'), getValues('orderTypeDescription')),
      salesOrg: firstNonEmpty(getValues('salesOrg')),
      supplyPointId: firstNonEmpty(getValues('supplyPointId'), getValues('supplyPointCode')) ?? '',
    });
    const current = getValues(`products.${index}`) as Product;
    setDialogMode('edit');
    setEditingIndex(index);
    setSelectedDialogMaterial({
      materialCode: current.materialCode,
      materialDescription: current.materialDescription,
      unitOfMeasure: current.unitOfMeasure,
      customerMaterialNumber: current.customerMaterialNumber,
      gomMaterialCode: current.gomMaterialCode,
      gomMaterialDescription: current.gomMaterialDescription,
    });
    setEditDraft({ ...current });
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingIndex(null);
    setEditDraft(null);
    setSelectedDialogMaterial(null);
    setDialogParams(null);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setPendingAction(null);
    setEditingIndex(null);
  };

  const continueStatusDialog = () => {
    const targetIndex = editingIndex;
    setValue('status', 'OC-Draft', { shouldDirty: true, shouldValidate: true });
    const action = pendingAction;
    closeStatusDialog();

    if (action === 'add') {
      openAddDialog();
      return;
    }

    if (action === 'edit' && targetIndex != null) {
      openEditDialog(targetIndex);
      return;
    }

    if (action === 'delete' && targetIndex != null) {
      removeProductAtIndex(targetIndex);
    }
  };

  const handleSelectDialogMaterial = (material: Material) => {
    setSelectedDialogMaterial(material);
    setEditDraft((prev) => {
      const next = prev ?? EMPTY_LINE;
      return {
        ...next,
        materialCode: material.materialCode ?? '',
        materialDescription: material.materialDescription ?? '',
        description: material.materialDescription ?? '',
        customerMaterialNumber: material.customerMaterialNumber ?? '',
        gomMaterialCode: material.gomMaterialCode ?? '',
        gomMaterialDescription: material.gomMaterialDescription ?? '',
        salesUOM: material.UOM ?? material.uom ?? material.unitOfMeasure ?? next.salesUOM,
        unitOfMeasure: material.unitOfMeasure ?? material.UOM ?? material.uom ?? next.unitOfMeasure,
        perUoM: material.UOM ?? material.uom ?? material.unitOfMeasure ?? next.perUoM,
      };
    });
  };

  const saveDialog = () => {
    if (!editDraft || !selectedDialogMaterial) {
      return;
    }

    const nextProduct: Product = {
      ...EMPTY_LINE,
      ...editDraft,
      materialCode: selectedDialogMaterial.materialCode ?? '',
      materialDescription: selectedDialogMaterial.materialDescription ?? '',
      description: selectedDialogMaterial.materialDescription ?? '',
      customerMaterialNumber: selectedDialogMaterial.customerMaterialNumber ?? '',
      gomMaterialCode: selectedDialogMaterial.gomMaterialCode ?? '',
      gomMaterialDescription: selectedDialogMaterial.gomMaterialDescription ?? '',
      lineNumber: dialogMode === 'add' ? getNextProductSequence() : Number(editDraft.lineNumber ?? editingIndex ?? 0) + 1,
      itemNumber: dialogMode === 'add'
        ? ''
        : String(editDraft.itemNumber ?? (editingIndex != null ? editingIndex + 1 : fields.length + 1)),
      quantity: Number(editDraft.quantity ?? 0),
      price: editDraft.price,
      perAmount: editDraft.perAmount,
      salesUOM: String(editDraft.salesUOM ?? ''),
      perUoM: String(editDraft.perUoM ?? ''),
      currency: String(editDraft.currency ?? ''),
    };

    if (dialogMode === 'add') {
      append(nextProduct);
    } else if (editingIndex != null) {
      setValue(`products.${editingIndex}`, nextProduct, { shouldDirty: true, shouldValidate: true });
    }

    closeDialog();
  };

  const handleAddProductClick = () => {
    if (readOnly) {
      return;
    }

    if (isAddProductDisabled) {
      return;
    }

    if (!isDraftStatus) {
      openStatusDialog('edit', 'add');
      return;
    }

    openAddDialog();
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader sx={{ pb: 2 }}
        title="Product Details"
        action={
          !readOnly ? (
            <Button
              id="qa-addProduct-btn"
              variant="contained"
              disabled={isAddProductDisabled}
              onClick={handleAddProductClick}
              sx={{
                backgroundColor: '#0c479d',
                color: '#ffffff',
                maxHeight: '36px',
                letterSpacing: '.08em',
                '&:hover': { backgroundColor: '#0a3c84' },
              }}
            >
              + ADD PRODUCT
            </Button>
          ) : null
        }
      />

      {fields.length > 0 ? (
        <CardContent sx={{ pt: 1, px: 3, pb: 3 }}>
          <TableContainer>
            <Table size="small" sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableCellSx}>Item Number</TableCell>
                  <TableCell sx={tableCellSx}>Quantity</TableCell>
                  <TableCell sx={tableCellSx}>Sales UoM</TableCell>
                  <TableCell sx={tableCellSx}>Material Code</TableCell>
                  <TableCell sx={tableCellSx}>Material Description</TableCell>
                  <TableCell sx={tableCellSx}>Customer Material # / Desc.</TableCell>
                  <TableCell sx={tableCellSx}>GOM Mat. Code</TableCell>
                  <TableCell sx={tableCellSx}>GOM Mat. Desc.</TableCell>
                  <TableCell sx={tableCellSx}>Price</TableCell>
                  <TableCell sx={tableCellSx}>Currency</TableCell>
                  <TableCell sx={tableCellSx}>Per</TableCell>
                  <TableCell sx={tableCellSx}>UoM</TableCell>
                  {!readOnly ? <TableCell sx={tableCellSx} /> : null}
                  {!readOnly ? <TableCell sx={{ ...tableCellSx, maxWidth: 30 }} /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedFields.map((field, rowIndex) => {
                  const index = page * rowsPerPage + rowIndex;
                  return (
                    <ProductRow
                      key={field.id}
                      index={index}
                      readOnly={readOnly}
                      onEdit={() => {
                        if (!isDraftStatus) {
                          setEditingIndex(index);
                          openStatusDialog('edit', 'edit');
                          return;
                        }

                        openEditDialog(index);
                      }}
                      onRemove={() => {
                        if (!isDraftStatus) {
                          setEditingIndex(index);
                          openStatusDialog('delete', 'delete');
                          return;
                        }

                        removeProductAtIndex(index);
                      }}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={fields.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 100]}
          />
        </CardContent>
      ) : null}

      <Dialog open={statusDialogOpen} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 2 }}>
            {statusDialogType === 'delete' ? 'Remove' : 'Edit'} Products For Submitted Order?
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2, lineHeight: 1.6 }}>
            The order you are currently editing is not in draft status and may be{'\n'}
            visible by other team members. Because the selected products may{'\n'}
            affect inventory calculations or scheduled transportation, choosing{'\n'}
            to edit these products will set the order back to <strong>OC-Draft</strong> status.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
            <ReportIcon sx={{ color: '#FE000C', mt: '2px' }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, color: '#FE000C' }}>
              Inventory calculations will be adjusted and shipments in OTD updated.
              Third party carriers must be manually notified of any changes.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            autoFocus
            ref={cancelStatusButtonRef}
            data-testid="qa-cancel-btn"
            variant="contained"
            onClick={closeStatusDialog}
            sx={{
              backgroundColor: '#0c479d',
              color: '#ffffff',
              maxHeight: '36px',
              letterSpacing: '.08em',
              '&:hover': {
                backgroundColor: '#0a3c84',
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            data-testid="qa-setStatus-btn"
            variant="contained"
            onClick={continueStatusDialog}
            sx={{
              backgroundColor: '#ffffff',
              color: '#FE000C',
              letterSpacing: '.08em',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            {statusDialogType === 'delete'
              ? 'SET STATUS AND REMOVE PRODUCTS'
              : 'SET STATUS AND EDIT PRODUCTS'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode != null}
        onClose={closeDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          className: 'order-popup-dialog-paper',
          style: {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: 'calc(100vh - 32px)',
            height: 'calc(100vh - 32px)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle id="qa-title-lbl">{dialogMode === 'add' ? 'Add a New Product' : 'Edit Product'}</DialogTitle>
        <DialogContent
          className="add-product-dialog-content"
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            px: 3,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <TextField
              id="qa-searchProduct-ipt"
              size="small"
              label="Search Product"
              value={productSearchInput}
              onChange={(event) => setProductSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setProductSearchTerm(productSearchInput.trim().toLowerCase());
                  setProductDialogPage(0);
                }
              }}
              sx={{ width: { xs: '100%', sm: '50%' } }}
            />
            <Button
              id="qa-search-btn"
              variant="contained"
              className="blue-button"
              onClick={() => {
                setProductSearchTerm(productSearchInput.trim().toLowerCase());
                setProductDialogPage(0);
              }}
            >
              SEARCH
            </Button>
            {productSearchInput.trim() ? (
              <Button
                id="qa-clear-btn"
                variant="contained"
                className="white-button"
                onClick={() => {
                  setProductSearchInput('');
                  setProductSearchTerm('');
                  setProductDialogPage(0);
                }}
              >
                CLEAR
              </Button>
            ) : null}
          </Box>

          {materialsQuery.isLoading ? (
            <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress id="qa-spinner-ico" size={50} />
            </Box>
          ) : (
            <Box sx={{ flex: '0 0 auto', maxHeight: 190, overflow: 'hidden' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  fontSize: 12,
                  '& th, & td': {
                    py: 0.25,
                    px: 0.75,
                    fontSize: 12,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 38 },
                  '& th:nth-of-type(2), & td:nth-of-type(2)': { width: '17%' },
                  '& th:nth-of-type(3), & td:nth-of-type(3)': { width: '22%' },
                  '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '12%' },
                  '& th:nth-of-type(5), & td:nth-of-type(5)': { width: '14%' },
                  '& th:nth-of-type(6), & td:nth-of-type(6)': { width: '10%' },
                  '& th:nth-of-type(7), & td:nth-of-type(7)': { width: '14%' },
                  '& th:nth-of-type(8), & td:nth-of-type(8)': { width: '7%' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell id="qa-products-materialCode-hdr">Material_ Code</TableCell>
                    <TableCell id="qa-products-description-hdr">Material Description</TableCell>
                    <TableCell id="qa-products-customerMaterialNumber-hdr">Customer Mat. #</TableCell>
                    <TableCell id="qa-products-customerDescription-hdr">Customer Mat. Description</TableCell>
                    <TableCell id="qa-products-gomMaterialCode-hdr">GOM Mat. Code</TableCell>
                    <TableCell id="qa-products-gomMaterialDescription-hdr">GOM Description</TableCell>
                    <TableCell id="qa-products-UOM-hdr">Base UoM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDialogMaterials.map((material, index) => {
                    const rowKey = `${material.materialCode}-${index}`;
                    const isSelected = selectedDialogMaterial != null
                      && material.materialCode === selectedDialogMaterial.materialCode
                      && material.materialDescription === selectedDialogMaterial.materialDescription;

                    return (
                      <TableRow key={rowKey} hover onClick={() => handleSelectDialogMaterial(material)}>
                        <TableCell id="qa-products-select">
                          <Radio
                            checked={isSelected}
                            onChange={() => handleSelectDialogMaterial(material)}
                            value={material.materialCode}
                          />
                        </TableCell>
                        <TableCell id="qa-products-materialCode">{stripLeadingZeros(material.materialCode) || '-'}</TableCell>
                        <TableCell id="qa-products-description">{material.materialDescription || '-'}</TableCell>
                        <TableCell id="qa-products-customerMaterialNumber">{material.customerMaterialNumber || '-'}</TableCell>
                        <TableCell id="qa-products-customerDescription">{material.customerDescription || '-'}</TableCell>
                        <TableCell id="qa-products-GOMMaterialCode">{material.gomMaterialCode || '-'}</TableCell>
                        <TableCell id="qa-GOMMaterialDescription">{material.gomMaterialDescription || '-'}</TableCell>
                        <TableCell id="qa-products-UOM">{material.UOM ?? material.uom ?? material.unitOfMeasure ?? '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                  {pagedDialogMaterials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 2 }}>
                        No products found for the selected order details.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                id="qa-table-searchProduct-paginator"
                component="div"
                count={dialogMaterials.length}
                page={productDialogPage}
                onPageChange={(_, nextPage) => setProductDialogPage(nextPage)}
                rowsPerPage={productDialogRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setProductDialogRowsPerPage(Number(event.target.value));
                  setProductDialogPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 100]}
                showFirstButton
                showLastButton
                sx={{
                  minHeight: 32,
                  height: 32,
                  '& .MuiTablePagination-toolbar': { minHeight: 32, height: 32, px: 0 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 11 },
                  '& .MuiTablePagination-input': { fontSize: 11 },
                }}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 500, mb: 2 }}>Order Details</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { md: 'center' } }}>
              <TextField
                label="Order Quantity"
                id="qa-orderQuantity-ipt"
                type="number"
                size="small"
                value={editDraft?.quantity ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, quantity: event.target.value === '' ? undefined : Number(event.target.value) }))}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 140 }}
              />
              <TextField
                id="qa-orderUoM-sel"
                select
                label="UoM"
                size="small"
                value={editDraft?.salesUOM ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, salesUOM: event.target.value }))}
                sx={{ width: 120 }}
              >
                {dialogUoms.map((uom) => (
                  <MenuItem key={uom} value={uom}>{uom}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Price"
                id="qa-price-ipt"
                type="number"
                size="small"
                value={editDraft?.price ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, price: event.target.value === '' ? undefined : Number(event.target.value) }))}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ width: 130 }}
              />
              <TextField
                id="qa-currency-sel"
                select
                label="Currency"
                size="small"
                value={editDraft?.currency ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, currency: event.target.value }))}
                sx={{ width: 140 }}
              >
                {dialogCurrencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </TextField>
              <Typography sx={{ fontSize: 14 }}>Per</Typography>
              <TextField
                label="Amount"
                id="qa-amount-ipt"
                type="number"
                size="small"
                value={editDraft?.perAmount ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, perAmount: event.target.value === '' ? undefined : Number(event.target.value) }))}
                inputProps={{ min: 0, step: 0.1 }}
                sx={{ width: 120 }}
              />
              <TextField
                id="qa-amountUoM-sel"
                select
                label="UoM"
                size="small"
                value={editDraft?.perUoM ?? ''}
                onChange={(event) => setEditDraft((prev) => ({ ...prev, perUoM: event.target.value }))}
                sx={{ width: 120 }}
              >
                {dialogUoms.map((uom) => (
                  <MenuItem key={uom} value={uom}>{uom}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Total Sale"
                id="qa-totalSale-ipt"
                size="small"
                value={totalSaleText}
                disabled
                sx={{ width: 160 }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button id="qa-cancel-btn" variant="contained" className="white-button" onClick={closeDialog}>CANCEL</Button>
          <Button
            id="qa-addProduct-submit-btn"
            variant="contained"
            className="blue-button"
            disabled={!canSubmitDialog}
            onClick={saveDialog}
          >
            {dialogMode === 'add' ? 'ADD PRODUCT' : 'UPDATE PRODUCT'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

interface RowProps {
  index: number;
  readOnly: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

const ProductRow: FC<RowProps> = ({ index, readOnly, onEdit, onRemove }) => {
  const { control } = useFormContext();

  return (
    <TableRow hover>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.itemNumber`}
          control={control}
          render={({ field }) => <Typography variant="body2">{(field.value as string) || ''}</Typography>}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.quantity`}
          control={control}
          render={({ field }) => (
            <Typography variant="body2">{formatQuantity(Number(field.value))}</Typography>
          )}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.salesUOM`}
          control={control}
          render={({ field }) => <Typography variant="body2">{(field.value as string) || '-'}</Typography>}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.materialCode`}
          control={control}
          render={({ field }) => {
            const rawMaterialCode = String(field.value ?? '').trim();
            const displayMaterialCode = stripLeadingZeros(rawMaterialCode);

            return (
              <Tooltip title={rawMaterialCode || ''}>
                <Typography variant="body2">{displayMaterialCode || '-'}</Typography>
              </Tooltip>
            );
          }}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.materialDescription`}
          control={control}
          render={({ field }) => (
            <Tooltip title={(field.value as string) || ''}>
              <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(field.value as string) || '-'}
              </Typography>
            </Tooltip>
          )}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.customerMaterialNumber`}
          control={control}
          render={({ field }) => (
            <Typography variant="body2">{(field.value as string) || '-'}</Typography>
          )}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.gomMaterialCode`}
          control={control}
          render={({ field }) => (
            <Typography variant="body2">{(field.value as string) || '-'}</Typography>
          )}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.gomMaterialDescription`}
          control={control}
          render={({ field }) => (
            <Typography variant="body2">{(field.value as string) || '-'}</Typography>
          )}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.price`}
          control={control}
          render={({ field }) => <Typography variant="body2">{field.value != null ? formatNumber(Number(field.value), 2) : '-'}</Typography>}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.currency`}
          control={control}
          render={({ field }) => <Typography variant="body2">{(field.value as string) || '-'}</Typography>}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.perAmount`}
          control={control}
          render={({ field }) => <Typography variant="body2">{field.value != null ? formatNumber(Number(field.value), 1) : '-'}</Typography>}
        />
      </TableCell>
      <TableCell sx={tableCellSx}>
        <Controller
          name={`products.${index}.perUoM`}
          control={control}
          render={({ field }) => <Typography variant="body2">{(field.value as string) || '-'}</Typography>}
        />
      </TableCell>
      {!readOnly ? (
        <TableCell sx={tableCellSx}>
          <Tooltip title="Edit product">
            <IconButton size="small" onClick={onEdit} sx={{ color: '#0c479d' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      ) : null}
      {!readOnly ? (
        <TableCell sx={{ ...tableCellSx, maxWidth: 30 }}>
          <Tooltip title="Delete product">
            <IconButton size="small" onClick={onRemove} sx={{ color: 'text.primary' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      ) : null}
    </TableRow>
  );
};
